import { Home, Compass, Plus, Bell, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useUnreadCount } from '@/hooks/use-notifications';
import { useFeatureFlag } from '@/contexts/FeatureFlagContext';
import { haptic } from '@/lib/native';

const doHaptic = () => { haptic('light'); };

const ALL_TABS = [
  { key: 'home', icon: Home, flag: null },
  { key: 'discover', icon: Compass, flag: 'discover' },
  { key: 'create', icon: Plus, isCreate: true, flag: 'create_post' },
  { key: 'messages', icon: Bell, flag: null },
  { key: 'profile', icon: User, flag: null },
];

interface Props {
  active: string;
  onChange: (tab: string) => void;
}

const DOT_SIZE = 16;
const EDGE_MARGIN = 20;
const DRAG_THRESHOLD = 6; // px movement before considered a drag

const BottomNav = ({ active, onChange }: Props) => {
  const navigate = useNavigate();
  const unreadCount = useUnreadCount();
  const { isEnabled } = useFeatureFlag();
  const prevUnreadRef = useRef(0);
  const [bellShake, setBellShake] = useState(false);

  const tabs = useMemo(() =>
    ALL_TABS.filter(tab => !tab.flag || isEnabled(tab.flag)),
    [isEnabled],
  );

  // --- Performant collapse logic ---
  const [collapsed, setCollapsed] = useState(false);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);
  const dirAccum = useRef(0);

  // --- Draggable dot state ---
  const [dotPos, setDotPos] = useState<{ x: number; y: number }>(() => {
    // Default: bottom-right
    const saved = typeof localStorage !== 'undefined' ? localStorage.getItem('nav-dot-pos') : null;
    if (saved) {
      try { return JSON.parse(saved); } catch { /* ignore */ }
    }
    return { x: window.innerWidth - EDGE_MARGIN - DOT_SIZE, y: window.innerHeight - 40 };
  });
  const dragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0, dotX: 0, dotY: 0 });
  const hasMoved = useRef(false);
  const dotRef = useRef<HTMLButtonElement>(null);

  // Snap to nearest edge
  const snapToEdge = useCallback((x: number, y: number) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const halfW = DOT_SIZE / 2;
    // Clamp Y
    const clampedY = Math.max(EDGE_MARGIN + halfW, Math.min(vh - EDGE_MARGIN - halfW, y));
    // Snap X to nearest side
    const snappedX = x < vw / 2 ? EDGE_MARGIN : vw - EDGE_MARGIN - DOT_SIZE;
    const pos = { x: snappedX, y: clampedY };
    setDotPos(pos);
    try { localStorage.setItem('nav-dot-pos', JSON.stringify(pos)); } catch { /* ignore */ }
  }, []);

  // Touch/mouse handlers
  const onDragStart = useCallback((clientX: number, clientY: number) => {
    dragging.current = true;
    hasMoved.current = false;
    dragStart.current = { x: clientX, y: clientY, dotX: dotPos.x, dotY: dotPos.y };
  }, [dotPos]);

  const onDragMove = useCallback((clientX: number, clientY: number) => {
    if (!dragging.current) return;
    const dx = clientX - dragStart.current.x;
    const dy = clientY - dragStart.current.y;
    if (!hasMoved.current && Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD) return;
    hasMoved.current = true;
    setDotPos({
      x: dragStart.current.dotX + dx,
      y: dragStart.current.dotY + dy,
    });
  }, []);

  const onDragEnd = useCallback(() => {
    if (!dragging.current) return;
    dragging.current = false;
    if (hasMoved.current) {
      snapToEdge(dotPos.x, dotPos.y);
    }
  }, [dotPos, snapToEdge]);

  // Attach global move/end listeners while dragging
  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => onDragMove(e.clientX, e.clientY);
    const onMouseUp = () => onDragEnd();
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches[0]) onDragMove(e.touches[0].clientX, e.touches[0].clientY);
    };
    const onTouchEnd = () => onDragEnd();

    window.addEventListener('mousemove', onMouseMove, { passive: true });
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [onDragMove, onDragEnd]);

  // Re-snap on window resize
  useEffect(() => {
    const onResize = () => snapToEdge(dotPos.x, dotPos.y);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [dotPos, snapToEdge]);

  useEffect(() => {
    const THRESHOLD = 30;

    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        const delta = y - lastScrollY.current;
        lastScrollY.current = y;

        if (y < 80) {
          dirAccum.current = 0;
          setCollapsed(false);
          ticking.current = false;
          return;
        }

        if (delta > 0) {
          dirAccum.current = dirAccum.current > 0 ? dirAccum.current + delta : delta;
        } else if (delta < 0) {
          dirAccum.current = dirAccum.current < 0 ? dirAccum.current + delta : delta;
        }

        if (dirAccum.current > THRESHOLD) {
          setCollapsed(true);
          dirAccum.current = 0;
        } else if (dirAccum.current < -THRESHOLD) {
          setCollapsed(false);
          dirAccum.current = 0;
        }

        ticking.current = false;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Shake bell icon when unread count transitions from 0 to >0
  useEffect(() => {
    if (prevUnreadRef.current === 0 && unreadCount > 0) {
      setBellShake(true);
      const timer = setTimeout(() => setBellShake(false), 800);
      return () => clearTimeout(timer);
    }
    prevUnreadRef.current = unreadCount;
  }, [unreadCount]);

  const handleTabClick = useCallback((key: string) => {
    haptic();
    onChange(key);
    const routes: Record<string, string> = {
      home: '/',
      profile: '/profile',
      messages: '/messages',
      discover: '/discover',
    };
    const target = routes[key];
    if (!target) return;
    if (window.location.pathname === target) {
      // Already on this route — for home, signal a full reset
      if (key === 'home') {
        navigate('/', { state: { resetKey: Date.now() }, replace: true });
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate(target);
    }
  }, [navigate, onChange]);

  const handleExpand = useCallback(() => {
    if (hasMoved.current) return; // was a drag, not a tap
    haptic('light');
    // Reset scroll accumulator to prevent immediate re-collapse
    dirAccum.current = 0;
    lastScrollY.current = window.scrollY;
    setCollapsed(false);
  }, []);

  return (
    <div className="fixed bottom-6 right-0 left-0 z-40 flex justify-center safe-area-bottom pointer-events-none">
      {/* Collapsed dot — draggable, snaps to edges */}
      <button
        ref={dotRef}
        onClick={handleExpand}
        onMouseDown={(e) => { e.preventDefault(); onDragStart(e.clientX, e.clientY); }}
        onTouchStart={(e) => { if (e.touches[0]) onDragStart(e.touches[0].clientX, e.touches[0].clientY); }}
        className={cn(
          'pointer-events-auto fixed z-50 rounded-full',
          collapsed
            ? 'opacity-100 scale-100'
            : 'opacity-0 scale-50 pointer-events-none',
          dragging.current ? '' : 'transition-all duration-300 ease-out'
        )}
        style={{
          width: DOT_SIZE,
          height: DOT_SIZE,
          left: dotPos.x,
          top: dotPos.y,
          willChange: 'transform, opacity',
          touchAction: 'none',
          cursor: 'grab',
        }}
        aria-label="展开导航"
      >
        {/* Breathing gold aura — GPU composited */}
        <span
          className="absolute inset-[-10px] rounded-full animate-[gold-aura_3s_ease-in-out_infinite]"
          style={{ willChange: 'opacity, transform' }}
        />
        {/* Orbiting arc ring 1 — warm gold */}
        <span
          className="absolute inset-[-8px] rounded-full animate-[orbit-spin_6s_linear_infinite]"
          style={{
            border: '1.5px solid transparent',
            borderTopColor: 'hsl(var(--gold) / 0.5)',
            borderRightColor: 'hsl(var(--gold-light) / 0.2)',
            willChange: 'transform',
          }}
        />
        {/* Orbiting arc ring 2 — counter-direction */}
        <span
          className="absolute inset-[-7px] rounded-full animate-[orbit-spin_8s_linear_infinite_reverse]"
          style={{
            border: '1px solid transparent',
            borderBottomColor: 'hsl(var(--gold-light) / 0.4)',
            borderLeftColor: 'hsl(var(--gold) / 0.15)',
            willChange: 'transform',
          }}
        />
        {/* Core gold dot — breathes from center outward */}
        <div
          className="relative w-full h-full rounded-full animate-[gold-core-breathe_3s_ease-in-out_infinite]"
          style={{
            background: 'linear-gradient(135deg, hsl(var(--gold)), hsl(var(--gold-light)))',
            willChange: 'transform, box-shadow',
          }}
        />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full animate-pulse"
            style={{
              background: 'hsl(var(--destructive))',
              boxShadow: '0 0 8px hsla(var(--destructive), 0.6)',
            }}
          />
        )}
      </button>

      {/* Full nav bar */}
      <nav
        className={cn(
          'pointer-events-auto flex items-center gap-1 px-2 py-2 rounded-[22px] transition-all duration-300 ease-out',
          collapsed && 'opacity-0 scale-75 pointer-events-none'
        )}
        style={{
          background: 'rgba(28, 28, 30, 0.95)',
          border: '0.5px solid rgba(255, 255, 255, 0.12)',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.45), 0 2px 12px rgba(0, 0, 0, 0.3), inset 0 0.5px 0 rgba(255, 255, 255, 0.08)',
          willChange: 'transform, opacity',
        }}
      >
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = active === tab.key;

          if (tab.isCreate) {
            return (
              <button
                key={tab.key}
                onClick={() => { haptic(); navigate('/create'); }}
                className="relative w-12 h-12 rounded-[16px] flex items-center justify-center active:scale-[0.8] transition-transform"
                style={{
                  background: 'linear-gradient(135deg, hsl(var(--gold)), hsl(var(--gold-light)))',
                  boxShadow: '0 0 20px hsla(var(--gold), 0.35)',
                }}
              >
                <Plus className="w-[22px] h-[22px] text-black" strokeWidth={2.5} />
              </button>
            );
          }

          return (
            <button
              key={tab.key}
              onClick={() => handleTabClick(tab.key)}
              className={cn(
                'relative w-12 h-12 rounded-[16px] flex items-center justify-center transition-colors duration-200 active:scale-[0.85] transition-transform',
                isActive ? 'bg-white/[0.12]' : 'bg-transparent'
              )}
            >
              {isActive && (
                <div
                  className="absolute inset-0 rounded-[16px]"
                  style={{
                    background: 'hsla(var(--gold), 0.12)',
                    boxShadow: '0 0 20px hsla(var(--gold), 0.1)',
                  }}
                />
              )}
              <div className={cn('relative z-10', tab.key === 'messages' && bellShake && 'animate-[bell-shake_0.6s_ease-in-out]')}>
                <Icon
                  className={cn(
                    'w-[21px] h-[21px] transition-colors duration-200',
                    isActive ? 'text-[hsl(var(--gold))]' : 'text-white/40'
                  )}
                  strokeWidth={isActive ? 2 : 1.5}
                />
              </div>
              {tab.key === 'messages' && unreadCount > 0 && (
                <span
                  className="absolute top-1.5 right-1.5 w-2 h-2 bg-pink-500 rounded-full border border-background z-20 animate-[unread-pulse_0.6s_ease-in-out_infinite_3s]"
                  style={{ boxShadow: '0 0 6px rgba(236, 72, 153, 0.5)' }}
                />
              )}
            </button>
          );
        })}
      </nav>
    </div>
  );
};

export default BottomNav;
